/**
 * HUD Scene: an overlay scene that runs in parallel with the GameScene.
 *
 * Displays:
 *  - Lives remaining (heart icons)
 *  - Health bar
 *  - Food collected counter
 *  - Current level
 *
 * Listens to events from the shared event bus to stay in sync.
 */

import Phaser from 'phaser';
import { SCENE_HUD, SCENE_GAME, Events } from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  HUD_HEIGHT,
  HUD_PADDING,
  MAX_HEALTH,
  BORDER_MARGIN,
  COLOR_WHITE,
  KEY_CHEST_MIN_LEVEL,
  TILE_SIZE,
  AGMUTH_MAX_PER_GAME,
  COLOR_LEVEL_EVENT,
  COLOR_ACHIEVEMENT_GOLD,
} from '../config/game-config.js';
import { LEVEL_EVENT_NAMES } from '../utils/constants.js';
import { ACHIEVEMENT_DEFINITIONS } from '../utils/achievement-manager.js';
import HealthBar from '../ui/HealthBar.js';
import LivesDisplay from '../ui/LivesDisplay.js';
import ScoreDisplay from '../ui/ScoreDisplay.js';
import CoinCounter from '../ui/CoinCounter.js';
import LevelDisplay from '../ui/LevelDisplay.js';
import MusicToggle from '../ui/MusicToggle.js';
import KeyDisplay from '../ui/KeyDisplay.js';
import AgmuthDisplay from '../ui/AgmuthDisplay.js';
import CompassDisplay from '../ui/CompassDisplay.js';
import ThreiaTotemDisplay from '../ui/ThreiaTotemDisplay.js';
import HammerDisplay from '../ui/HammerDisplay.js';
import ShopItemsDisplay from '../ui/ShopItemsDisplay.js';
import MiniMapDisplay from '../ui/MiniMapDisplay.js';

export default class HudScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_HUD });
  }

  /**
   * @param {{
   *   eventBus: Phaser.Events.EventEmitter,
   *   health: number,
   *   maxHealth: number,
   *   lives: number,
   *   foodCollected: number,
   *   level: number,
   *   playerKeys: string[],
   *   agmuthCount: number,
   *   hasCompass: boolean,
   *   hasThreiaTotem: boolean,
   *   hammerCharges: number,
   *   exitPos: {col: number, row: number},
   *   minimapVisible: boolean,
   * }} data
   */
  init(data) {
    this.eventBus = data.eventBus;
    this.initialHealth = data.health ?? MAX_HEALTH;
    this.initialMaxHealth = data.maxHealth ?? MAX_HEALTH;
    this.initialLives = data.lives ?? 3;
    this.initialScore = data.score ?? 0;
    this.initialCoins = data.coins ?? 0;
    this.initialLevel = data.level ?? 1;
    this.initialMusicEnabled = data.musicEnabled !== undefined ? data.musicEnabled : true;
    this.initialPlayerKeys = data.playerKeys ?? [];
    this.initialAgmuthCount = data.agmuthCount ?? 0;
    this.initialHasCompass = data.hasCompass ?? false;
    this.initialHasThreiaTotem = data.hasThreiaTotem ?? false;
    this.initialHammerCharges = data.hammerCharges ?? 0;
    this.initialExitPos = data.exitPos ?? null;
    this.initialMinimapVisible = data.minimapVisible ?? false;
  }

  create() {
    // Decorative border (same as TitleScene)
    this._drawBorder();

    // Semi-transparent HUD background strip inside the border
    this._drawBackground();

    // UI Components — positioned inside the border
    const left = BORDER_MARGIN + HUD_PADDING;
    const right = GAME_WIDTH - BORDER_MARGIN;
    const y = BORDER_MARGIN + HUD_PADDING + 8;

    this.livesDisplay = new LivesDisplay(this, left, y);
    this.livesDisplay.setLives(this.initialLives);

    this.healthBar = new HealthBar(this, left + 68, y - 7);
    this.healthBar.draw(this.initialHealth / this.initialMaxHealth);

    this.scoreDisplay = new ScoreDisplay(this, left + 208, y);
    this.scoreDisplay.setScore(this.initialScore);

    this.coinCounter = new CoinCounter(this, left + 340, y);
    this.coinCounter.setCount(this.initialCoins);

    // Key display (only visible from level 3+)
    this.keyDisplay = new KeyDisplay(this, left + 395, y);
    if (this.initialLevel >= KEY_CHEST_MIN_LEVEL) {
      this.keyDisplay.setKeys(this.initialPlayerKeys);
    } else {
      this.keyDisplay.setKeys([]);
      this._setKeyDisplayVisible(false);
    }

    // Agmuth display (only visible from level 3+ or if already have some)
    this.agmuthDisplay = new AgmuthDisplay(this, left + 490, y);
    if (this.initialLevel >= KEY_CHEST_MIN_LEVEL || this.initialAgmuthCount > 0) {
      this.agmuthDisplay.setCount(this.initialAgmuthCount);
      // Activate full-set glow if all 4 relics are already collected
      if (this.initialAgmuthCount >= AGMUTH_MAX_PER_GAME) {
        this.agmuthDisplay.activateFullSetGlow();
      }
    } else {
      this.agmuthDisplay.setCount(0);
      this._setAgmuthDisplayVisible(false);
    }

    this.levelDisplay = new LevelDisplay(this, right - 100, y);
    this.levelDisplay.setLevel(this.initialLevel);

    // Music toggle (right edge of HUD, clickable + M key shortcut)
    this.musicToggle = new MusicToggle(
      this,
      right - 14,
      y,
      this.initialMusicEnabled,
      this.eventBus,
    );

    // Compass display (bottom-right corner of playable area, hidden initially)
    this._compassExitPos = null;
    this.compassDisplay = new CompassDisplay(
      this,
      GAME_WIDTH - BORDER_MARGIN - 35,
      GAME_HEIGHT - BORDER_MARGIN - 35,
    );

    // If compass was already collected (restored save), show immediately
    if (this.initialHasCompass && this.initialExitPos) {
      this._compassExitPos = this.initialExitPos;
      this.compassDisplay.show();
      // We don't have the player position yet at init, but PLAYER_MOVED
      // will fire soon and update the direction.
    }

    // Threia Totem display (bottom-left corner of playable area, hidden initially)
    this.threiaTotemDisplay = new ThreiaTotemDisplay(
      this,
      BORDER_MARGIN + 35,
      GAME_HEIGHT - BORDER_MARGIN - 35,
    );

    // If totem was already collected (restored save), show immediately
    if (this.initialHasThreiaTotem) {
      this.threiaTotemDisplay.setState(true);
    }

    // Hammer of Threia display (bottom-left, offset from totem)
    this.hammerDisplay = new HammerDisplay(
      this,
      BORDER_MARGIN + 105,
      GAME_HEIGHT - BORDER_MARGIN - 35,
    );

    // If hammer charges exist (restored save or shop), show immediately
    if (this.initialHammerCharges > 0) {
      this.hammerDisplay.setState(this.initialHammerCharges);
    }

    // Shop items indicator strip (below HUD bar, shows owned items)
    this.shopItemsDisplay = new ShopItemsDisplay(this, this.eventBus);

    // Mini-map display (bottom-right, above compass, toggled via TAB)
    this.miniMapDisplay = new MiniMapDisplay(this);
    if (this.initialMinimapVisible) {
      this.miniMapDisplay.show();
    }

    // Level event indicator (golden italic text below HUD bar, right-aligned)
    this._levelEventText = this.add.text(
      right - HUD_PADDING,
      BORDER_MARGIN + HUD_HEIGHT + 4,
      '',
      {
        fontFamily: 'monospace',
        fontSize: '10px',
        fontStyle: 'italic',
        color: '#ffd700',
        align: 'right',
      },
    ).setOrigin(1, 0).setAlpha(0).setDepth(200);

    // Create bound callbacks and subscribe to events
    this._createBoundCallbacks();
    this._subscribeEvents();

    // Register shutdown with Phaser's scene lifecycle so cleanup actually runs
    this.events.on('shutdown', this._onShutdown, this);
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /** @private Draw the HUD background strip inside the border. */
  _drawBackground() {
    const g = this.add.graphics();
    const x = BORDER_MARGIN;
    const y = BORDER_MARGIN;
    const w = GAME_WIDTH - BORDER_MARGIN * 2;

    g.fillStyle(0x000000, 0.85);
    g.fillRect(x, y, w, HUD_HEIGHT - 1);

    // Bottom border line of HUD
    g.lineStyle(1, 0xffffff, 0.3);
    g.lineBetween(x, y + HUD_HEIGHT - 1, x + w, y + HUD_HEIGHT - 1);
  }

  /** @private Draw the decorative border matching TitleScene. */
  _drawBorder() {
    const g = this.add.graphics();
    g.setDepth(200);

    // Outer border rectangle
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    // Inner decorative corners
    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    // Top-left
    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);

    // Top-right
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);

    // Bottom-left
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);

    // Bottom-right
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);

    // Black fill outside the border to mask any game content bleeding through
    const mask = this.add.graphics();
    mask.setDepth(199);
    mask.fillStyle(0x000000, 1);
    // Top strip
    mask.fillRect(0, 0, GAME_WIDTH, BORDER_MARGIN);
    // Bottom strip
    mask.fillRect(0, GAME_HEIGHT - BORDER_MARGIN, GAME_WIDTH, BORDER_MARGIN);
    // Left strip
    mask.fillRect(0, 0, BORDER_MARGIN, GAME_HEIGHT);
    // Right strip
    mask.fillRect(GAME_WIDTH - BORDER_MARGIN, 0, BORDER_MARGIN, GAME_HEIGHT);
  }

  /**
   * @private Create bound callbacks for all eventBus listeners.
   * Storing references allows proper removal in _unsubscribeEvents().
   */
  _createBoundCallbacks() {
    this._onHealthChanged = (health, maxHealth) => {
      if (this.healthBar) this.healthBar.draw(health / maxHealth);
    };

    this._onLifeLost = (lives) => {
      if (this.livesDisplay) this.livesDisplay.playLifeLostEffect(lives);
    };

    this._onScoreChanged = (score) => {
      if (this.scoreDisplay) this.scoreDisplay.setScore(score);
    };

    this._onCoinsChanged = (coins) => {
      if (this.coinCounter) this.coinCounter.setCount(coins);
    };

    this._onLifeGained = (lives) => {
      if (this.livesDisplay) this.livesDisplay.playLifeGainedEffect(lives);
    };

    this._onFoodCollected = () => {
      if (this.livesDisplay) this.livesDisplay.playCollectEffect();
    };

    this._onCoinCollected = () => {
      if (this.coinCounter) this.coinCounter.playCollectEffect();
    };

    this._onPlayerResting = () => {
      if (this.healthBar) this.healthBar.startGlow();
    };

    this._onPlayerRestStopped = () => {
      if (this.healthBar) this.healthBar.stopGlow();
    };

    this._onPlayerPoisoned = () => {
      if (this.healthBar) this.healthBar.startPoisonGlow();
    };

    this._onPlayerPoisonEnded = () => {
      if (this.healthBar) this.healthBar.stopPoisonGlow();
    };

    this._onPlayerWounded = () => {
      if (this.healthBar) this.healthBar.startWoundedGlow();
    };

    this._onPlayerWoundEnded = () => {
      if (this.healthBar) this.healthBar.stopWoundedGlow();
    };

    this._onKeysChanged = (keys) => {
      if (this.keyDisplay) {
        this._setKeyDisplayVisible(true);
        this.keyDisplay.setKeys(keys);
      }
    };

    this._onKeyCollected = (tier) => {
      if (this.keyDisplay) {
        this._setKeyDisplayVisible(true);
        this.keyDisplay.addKey(tier);
      }
    };

    this._onKeyUsed = (tier) => {
      if (this.keyDisplay) {
        this.keyDisplay.removeKey(tier);
      }
    };

    this._onAgmuthChanged = (count) => {
      if (this.agmuthDisplay) {
        this._setAgmuthDisplayVisible(true);
        this.agmuthDisplay.setCount(count);
        // Activate full-set glow when all 4 relics are collected
        if (count >= AGMUTH_MAX_PER_GAME) {
          this.agmuthDisplay.activateFullSetGlow();
        }
      }
    };

    this._onAgmuthCollected = () => {
      if (this.agmuthDisplay) {
        this._setAgmuthDisplayVisible(true);
        this.agmuthDisplay.addAgmuth();
        // Activate full-set glow when all 4 relics are collected
        if (this.agmuthDisplay._count >= AGMUTH_MAX_PER_GAME) {
          // Small delay so the collect animation plays first
          this.time.delayedCall(600, () => {
            if (this.agmuthDisplay) {
              this.agmuthDisplay.activateFullSetGlow();
            }
          });
        }
      }
    };

    this._onAgmuthActivated = () => {
      if (this.agmuthDisplay) {
        this.agmuthDisplay.activateGlow();
      }
    };

    this._onAgmuthDeactivated = (newCount) => {
      if (this.agmuthDisplay) {
        this.agmuthDisplay.consumeAgmuth(newCount);
      }
    };

    this._onLevelChanged = (level) => {
      if (this.levelDisplay) this.levelDisplay.setLevel(level);
      if (level >= KEY_CHEST_MIN_LEVEL) {
        this._setKeyDisplayVisible(true);
        this._setAgmuthDisplayVisible(true);
      }
      // Hide compass on new level (new compass must be found)
      if (this.compassDisplay) {
        this.compassDisplay.hide();
        this._compassExitPos = null;
      }
      // Refresh mini-map for the new level (maze dimensions may have changed)
      if (this.miniMapDisplay) {
        this.miniMapDisplay.update();
      }
    };

    this._onCompassCollected = (data) => {
      if (this.compassDisplay && data) {
        this._compassExitPos = { col: data.exitCol, row: data.exitRow };
        this.compassDisplay.show();
      }
    };

    this._onPlayerMoved = (col, row) => {
      if (this.compassDisplay && this._compassExitPos) {
        this.compassDisplay.updateDirection(
          col, row,
          this._compassExitPos.col, this._compassExitPos.row,
        );

        // Compute player's screen position to fade compass when nearby.
        // The GameScene camera viewport starts at (BORDER_MARGIN, BORDER_MARGIN + HUD_HEIGHT).
        const gameScene = this.scene.get(SCENE_GAME);
        if (gameScene && gameScene.cameras && gameScene.cameras.main) {
          const cam = gameScene.cameras.main;
          const worldX = col * TILE_SIZE + TILE_SIZE / 2;
          const worldY = row * TILE_SIZE + TILE_SIZE / 2;
          // Convert world position to screen position
          const screenX = (worldX - cam.scrollX) * cam.zoom + cam.x;
          const screenY = (worldY - cam.scrollY) * cam.zoom + cam.y;
          this.compassDisplay.updateProximity(screenX, screenY);
        }
      }

      // Update mini-map on each player move
      if (this.miniMapDisplay) {
        this.miniMapDisplay.update();
      }
    };

    this._onCompassDisintegrate = () => {
      if (this.compassDisplay) {
        this.compassDisplay.disintegrate();
        this._compassExitPos = null;
      }
    };

    this._onThreiaTotemCollected = () => {
      if (this.threiaTotemDisplay) {
        this.threiaTotemDisplay.showTotem();
      }
    };

    this._onThreiaTotemConsumed = () => {
      if (this.threiaTotemDisplay) {
        this.threiaTotemDisplay.consumeTotem();
      }
    };

    this._onThreiaPrayerPhase = (phase) => {
      if (this.threiaTotemDisplay) {
        this.threiaTotemDisplay.onPrayerPhase(phase);
      }
    };

    this._onBatRepelled = () => {
      if (this.threiaTotemDisplay) {
        this.threiaTotemDisplay.playRepelFlash();
      }
    };

    this._onHeartPotionChanged = () => {
      if (this.shopItemsDisplay) {
        this.shopItemsDisplay.refresh();
      }
    };

    this._onHammerCollected = (charges) => {
      if (this.hammerDisplay) {
        this.hammerDisplay.show(charges);
      }
    };

    this._onHammerChargesChanged = (charges) => {
      if (this.hammerDisplay) {
        this.hammerDisplay.updateCharges(charges);
      }
    };

    this._onHammerHit = (hitCount, maxHits) => {
      if (this.hammerDisplay) {
        this.hammerDisplay.onHit(hitCount, maxHits);
      }
    };

    this._onLevelEventActivated = (eventType) => {
      if (this._levelEventText) {
        const name = LEVEL_EVENT_NAMES[eventType] || eventType;
        this._levelEventText.setText(name);
        this._levelEventText.setAlpha(1);
        // Subtle pulsing glow animation
        if (this._levelEventPulseTween) this._levelEventPulseTween.destroy();
        this._levelEventPulseTween = this.tweens.add({
          targets: this._levelEventText,
          alpha: { from: 1, to: 0.45 },
          duration: 1600,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    };

    this._onLevelEventDeactivated = () => {
      if (this._levelEventText) {
        this._levelEventText.setText('');
        this._levelEventText.setAlpha(0);
        if (this._levelEventPulseTween) {
          this._levelEventPulseTween.destroy();
          this._levelEventPulseTween = null;
        }
      }
    };

    this._onAchievementUnlocked = (achievementId) => {
      this._showAchievementToast(achievementId);
    };

    this._onMinimapToggled = () => {
      if (this.miniMapDisplay) {
        this.miniMapDisplay.toggle();
      }
    };
  }

  /** @private Subscribe to eventBus using stored callback references. */
  _subscribeEvents() {
    if (!this.eventBus) return;

    this.eventBus.on(Events.HEALTH_CHANGED, this._onHealthChanged);
    this.eventBus.on(Events.LIFE_LOST, this._onLifeLost);
    this.eventBus.on(Events.SCORE_CHANGED, this._onScoreChanged);
    this.eventBus.on(Events.COINS_CHANGED, this._onCoinsChanged);
    this.eventBus.on(Events.LIFE_GAINED, this._onLifeGained);
    this.eventBus.on(Events.FOOD_COLLECTED, this._onFoodCollected);
    this.eventBus.on(Events.COIN_COLLECTED, this._onCoinCollected);
    this.eventBus.on(Events.PLAYER_RESTING, this._onPlayerResting);
    this.eventBus.on(Events.PLAYER_REST_STOPPED, this._onPlayerRestStopped);
    this.eventBus.on(Events.PLAYER_POISONED, this._onPlayerPoisoned);
    this.eventBus.on(Events.PLAYER_POISON_ENDED, this._onPlayerPoisonEnded);
    this.eventBus.on(Events.PLAYER_WOUNDED, this._onPlayerWounded);
    this.eventBus.on(Events.PLAYER_WOUND_ENDED, this._onPlayerWoundEnded);
    this.eventBus.on(Events.KEYS_CHANGED, this._onKeysChanged);
    this.eventBus.on(Events.KEY_COLLECTED, this._onKeyCollected);
    this.eventBus.on(Events.KEY_USED, this._onKeyUsed);
    this.eventBus.on(Events.AGMUTH_CHANGED, this._onAgmuthChanged);
    this.eventBus.on(Events.AGMUTH_COLLECTED, this._onAgmuthCollected);
    this.eventBus.on(Events.AGMUTH_ACTIVATED, this._onAgmuthActivated);
    this.eventBus.on(Events.AGMUTH_DEACTIVATED, this._onAgmuthDeactivated);
    this.eventBus.on(Events.LEVEL_CHANGED, this._onLevelChanged);
    this.eventBus.on(Events.COMPASS_COLLECTED, this._onCompassCollected);
    this.eventBus.on(Events.PLAYER_MOVED, this._onPlayerMoved);
    this.eventBus.on(Events.COMPASS_DISINTEGRATE, this._onCompassDisintegrate);
    this.eventBus.on(Events.THREIA_TOTEM_COLLECTED, this._onThreiaTotemCollected);
    this.eventBus.on(Events.THREIA_TOTEM_CONSUMED, this._onThreiaTotemConsumed);
    this.eventBus.on(Events.THREIA_PRAYER_PHASE, this._onThreiaPrayerPhase);
    this.eventBus.on(Events.BAT_REPELLED, this._onBatRepelled);
    this.eventBus.on(Events.HEART_POTION_CHANGED, this._onHeartPotionChanged);
    this.eventBus.on(Events.HAMMER_COLLECTED, this._onHammerCollected);
    this.eventBus.on(Events.HAMMER_CHARGES_CHANGED, this._onHammerChargesChanged);
    this.eventBus.on(Events.HAMMER_HIT, this._onHammerHit);
    this.eventBus.on(Events.LEVEL_EVENT_ACTIVATED, this._onLevelEventActivated);
    this.eventBus.on(Events.LEVEL_EVENT_DEACTIVATED, this._onLevelEventDeactivated);
    this.eventBus.on(Events.ACHIEVEMENT_UNLOCKED, this._onAchievementUnlocked);
    this.eventBus.on(Events.MINIMAP_TOGGLED, this._onMinimapToggled);
  }

  /**
   * @private Unsubscribe from eventBus using the same callback references.
   * Only removes HudScene's listeners, leaving GameScene's listeners intact.
   */
  _unsubscribeEvents() {
    if (!this.eventBus) return;

    this.eventBus.off(Events.HEALTH_CHANGED, this._onHealthChanged);
    this.eventBus.off(Events.LIFE_LOST, this._onLifeLost);
    this.eventBus.off(Events.SCORE_CHANGED, this._onScoreChanged);
    this.eventBus.off(Events.COINS_CHANGED, this._onCoinsChanged);
    this.eventBus.off(Events.LIFE_GAINED, this._onLifeGained);
    this.eventBus.off(Events.FOOD_COLLECTED, this._onFoodCollected);
    this.eventBus.off(Events.COIN_COLLECTED, this._onCoinCollected);
    this.eventBus.off(Events.PLAYER_RESTING, this._onPlayerResting);
    this.eventBus.off(Events.PLAYER_REST_STOPPED, this._onPlayerRestStopped);
    this.eventBus.off(Events.PLAYER_POISONED, this._onPlayerPoisoned);
    this.eventBus.off(Events.PLAYER_POISON_ENDED, this._onPlayerPoisonEnded);
    this.eventBus.off(Events.PLAYER_WOUNDED, this._onPlayerWounded);
    this.eventBus.off(Events.PLAYER_WOUND_ENDED, this._onPlayerWoundEnded);
    this.eventBus.off(Events.KEYS_CHANGED, this._onKeysChanged);
    this.eventBus.off(Events.KEY_COLLECTED, this._onKeyCollected);
    this.eventBus.off(Events.KEY_USED, this._onKeyUsed);
    this.eventBus.off(Events.AGMUTH_CHANGED, this._onAgmuthChanged);
    this.eventBus.off(Events.AGMUTH_COLLECTED, this._onAgmuthCollected);
    this.eventBus.off(Events.AGMUTH_ACTIVATED, this._onAgmuthActivated);
    this.eventBus.off(Events.AGMUTH_DEACTIVATED, this._onAgmuthDeactivated);
    this.eventBus.off(Events.LEVEL_CHANGED, this._onLevelChanged);
    this.eventBus.off(Events.COMPASS_COLLECTED, this._onCompassCollected);
    this.eventBus.off(Events.PLAYER_MOVED, this._onPlayerMoved);
    this.eventBus.off(Events.COMPASS_DISINTEGRATE, this._onCompassDisintegrate);
    this.eventBus.off(Events.THREIA_TOTEM_COLLECTED, this._onThreiaTotemCollected);
    this.eventBus.off(Events.THREIA_TOTEM_CONSUMED, this._onThreiaTotemConsumed);
    this.eventBus.off(Events.THREIA_PRAYER_PHASE, this._onThreiaPrayerPhase);
    this.eventBus.off(Events.BAT_REPELLED, this._onBatRepelled);
    this.eventBus.off(Events.HEART_POTION_CHANGED, this._onHeartPotionChanged);
    this.eventBus.off(Events.HAMMER_COLLECTED, this._onHammerCollected);
    this.eventBus.off(Events.HAMMER_CHARGES_CHANGED, this._onHammerChargesChanged);
    this.eventBus.off(Events.HAMMER_HIT, this._onHammerHit);
    this.eventBus.off(Events.LEVEL_EVENT_ACTIVATED, this._onLevelEventActivated);
    this.eventBus.off(Events.LEVEL_EVENT_DEACTIVATED, this._onLevelEventDeactivated);
    this.eventBus.off(Events.ACHIEVEMENT_UNLOCKED, this._onAchievementUnlocked);
    this.eventBus.off(Events.MINIMAP_TOGGLED, this._onMinimapToggled);
  }

  /**
   * Called when the scene shuts down (via Phaser lifecycle).
   * Properly removes only HudScene's listeners from the shared eventBus.
   */
  _onShutdown() {
    // Unsubscribe from event bus using stored callback references
    this._unsubscribeEvents();

    if (this.healthBar) this.healthBar.destroy();
    if (this.livesDisplay) this.livesDisplay.destroy();
    if (this.scoreDisplay) this.scoreDisplay.destroy();
    if (this.coinCounter) this.coinCounter.destroy();
    if (this.keyDisplay) this.keyDisplay.destroy();
    if (this.agmuthDisplay) this.agmuthDisplay.destroy();
    if (this.compassDisplay) this.compassDisplay.destroy();
    if (this.threiaTotemDisplay) this.threiaTotemDisplay.destroy();
    if (this.hammerDisplay) this.hammerDisplay.destroy();
    if (this.shopItemsDisplay) this.shopItemsDisplay.destroy();
    if (this.miniMapDisplay) this.miniMapDisplay.destroy();
    if (this._levelEventPulseTween) {
      this._levelEventPulseTween.destroy();
      this._levelEventPulseTween = null;
    }
    if (this._levelEventText) {
      this._levelEventText.destroy();
      this._levelEventText = null;
    }
    if (this.levelDisplay) this.levelDisplay.destroy();
    if (this.musicToggle) this.musicToggle.destroy();
    if (this._achievementToast) {
      this._achievementToast.destroy();
      this._achievementToast = null;
    }
    if (this._achievementToastTween) {
      this._achievementToastTween.destroy();
      this._achievementToastTween = null;
    }
  }

  /**
   * Show or hide the key display widgets.
   * @private
   * @param {boolean} visible
   */
  _setKeyDisplayVisible(visible) {
    if (!this.keyDisplay) return;
    // Access the internal label and sprites to set visibility
    if (this.keyDisplay._label) this.keyDisplay._label.setVisible(visible);
    for (const sprite of this.keyDisplay._sprites) {
      if (sprite) sprite.setVisible(visible);
    }
  }

  /**
   * Show or hide the agmuth display widgets.
   * @private
   * @param {boolean} visible
   */
  _setAgmuthDisplayVisible(visible) {
    if (!this.agmuthDisplay) return;
    if (this.agmuthDisplay._label) this.agmuthDisplay._label.setVisible(visible);
    for (const sprite of this.agmuthDisplay._sprites) {
      if (sprite) sprite.setVisible(visible);
    }
  }

  /**
   * Display a brief achievement-unlocked toast notification at the bottom
   * center of the screen. The toast fades in, holds, then fades out.
   * @private
   * @param {string} achievementId
   */
  _showAchievementToast(achievementId) {
    // Look up the achievement name
    const def = ACHIEVEMENT_DEFINITIONS.find((a) => a.id === achievementId);
    if (!def) return;

    // Destroy any existing toast
    if (this._achievementToast) {
      this._achievementToast.destroy();
      this._achievementToast = null;
    }
    if (this._achievementToastTween) {
      this._achievementToastTween.destroy();
      this._achievementToastTween = null;
    }

    const cx = GAME_WIDTH / 2;
    const y = GAME_HEIGHT - BORDER_MARGIN - 18;

    this._achievementToast = this.add.text(cx, y, `★ ${def.name} ★`, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffd700',
      stroke: '#000000',
      strokeThickness: 3,
      align: 'center',
    }).setOrigin(0.5, 0.5).setAlpha(0).setDepth(500);

    // Fade in, hold, fade out
    this._achievementToastTween = this.tweens.add({
      targets: this._achievementToast,
      alpha: { from: 0, to: 1 },
      duration: 400,
      ease: 'Sine.easeIn',
      hold: 2500,
      yoyo: true,
      onComplete: () => {
        if (this._achievementToast) {
          this._achievementToast.destroy();
          this._achievementToast = null;
        }
        this._achievementToastTween = null;
      },
    });
  }
}
